// NIGHT-DIRECTSTAFF-2026-10-25 §5: "Add to my calendar" for staff, on a booked interview card. The file is built the facility portal's
// way -- its pure builder, vendored from bosshog123/facility-portal functions/_lib/ics.js (DAY-FACILITIES-2026-10-25 §2, main 18d8705):
// DTSTART in the slot's own timezone with a one-component VTIMEZONE (valid RFC 5545 in Outlook, Google and Apple), 45 minutes,
// initials only. The facts are facility_portal_ics's (card id, initials, the booked time and its timezone, the request label
// "<discipline> request (<city>, <state>)" or "staffing request"); staff read them from the card the CRM already shows, so no
// staff database function is needed. The staff file differs only in its UID (crm-…) and its link (the CRM, not the portal).

const pad = (n, w = 2) => String(n).padStart(w, '0');

/** The wall-clock parts of `at` in IANA zone `tz` -> { y, mo, d, h, mi, s }. Throws on an unknown zone. */
export function wallClock(at, tz) {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const p = Object.fromEntries(f.formatToParts(at).map((x) => [x.type, x.value]));
  return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute, s: +p.second };
}

/** The zone's UTC offset at `at`, in minutes (Chicago in summer -> -300). */
export function offsetMinutes(at, tz) {
  const w = wallClock(at, tz);
  return Math.round((Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s) - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

const stamp = (w) => `${pad(w.y, 4)}${pad(w.mo)}${pad(w.d)}T${pad(w.h)}${pad(w.mi)}${pad(w.s)}`;
const utcStamp = (d) => `${stamp({ y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, d: d.getUTCDate(), h: d.getUTCHours(), mi: d.getUTCMinutes(), s: d.getUTCSeconds() })}Z`;
const offset = (m) => `${m < 0 ? '-' : '+'}${pad(Math.floor(Math.abs(m) / 60))}${pad(Math.abs(m) % 60)}`;
/** RFC 5545 TEXT escaping. */
export const esc = (s) => String(s).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/** Folds one content line at 75 octets (UTF-8), never inside a character. */
export function fold(line) {
  const enc = new TextEncoder();
  const out = [];
  let cur = '';
  let n = 0;
  for (const ch of line) {
    const b = enc.encode(ch).length;
    if (n + b > (out.length ? 74 : 75)) { out.push(cur); cur = ''; n = 0; }
    cur += ch;
    n += b;
  }
  out.push(cur);
  return out.join('\r\n ');
}

/** The request label facility_portal_ics uses (facility_private.request_label): "PT request (Denver, CO)" | "staffing request" */
export function requestLabel(r) {
  if (!r) return 'staffing request';
  const loc = [r.city, r.state].map((x) => String(x || '').trim()).filter(Boolean).join(', ');
  return `${String(r.discipline || '').trim() || 'staffing'} request${loc ? ` (${loc})` : ''}`;
}

/**
 * Staff's file: { cardId, initials, at (ISO), tz, label, facility, link, now? } -> the .ics text (CRLF). 45 minutes, as the portal's.
 */
export function staffInterviewIcs({ cardId, initials, at, tz, label, facility = '', link = 'https://crm.protherapystaffing.com/', now = new Date() }) {
  const start = new Date(at);
  if (Number.isNaN(start.getTime())) throw new Error('no booked time');
  const zone = tz || 'America/New_York';
  const off = offsetMinutes(start, zone);
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//ProTherapy Staffing//TherapyLink CRM//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'BEGIN:VTIMEZONE', `TZID:${zone}`, 'BEGIN:STANDARD', 'DTSTART:19700101T000000', `TZOFFSETFROM:${offset(off)}`, `TZOFFSETTO:${offset(off)}`,
    'END:STANDARD', 'END:VTIMEZONE',
    'BEGIN:VEVENT',
    `UID:crm-interview-${cardId}@crm.protherapystaffing.com`,
    `DTSTAMP:${utcStamp(now)}`,
    `DTSTART;TZID=${zone}:${stamp(wallClock(start, zone))}`,
    'DURATION:PT45M',
    `SUMMARY:${esc(`Interview — candidate ${initials}${facility ? ` · ${facility}` : ''}`)}`,
    `DESCRIPTION:${esc(`Facility interview for the ${label}. The card and the facility: ${link}`)}`,
    `URL:${link}`,
    'END:VEVENT', 'END:VCALENDAR',
  ];
  return `${lines.map(fold).join('\r\n')}\r\n`;
}

/** Save the file in the browser (no server round trip; nothing leaves the CRM). */
export function downloadIcs(text, initials) {
  const safe = String(initials || 'candidate').replace(/[^A-Za-z]/g, '').slice(0, 4) || 'candidate';
  const url = URL.createObjectURL(new Blob([text], { type: 'text/calendar;charset=utf-8' }));
  const a = document.createElement('a'); a.href = url; a.download = `interview-${safe}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return `interview-${safe}.ics`;
}
